import React from 'react';
import { ShareSection } from '../../components/template';
import { Helmet } from "react-helmet";

export const metadata = {
  title: "Never Do These 7 Mistakes After 30 (Or You’ll Regret It Later)",
  description:
    "Avoid these 7 lifestyle mistakes after 30 to protect your energy, health, and peace of mind. Small changes now save big regrets later.",
  keywords:
    "lifestyle after 30, mistakes to avoid after 30, health tips for 30s, self care habits 30s, gut health, stress management, sleep habits, Ayurveda, Param Life, Indian wellness",
};

const Blog4 = () => {

  const hashtagsBottom = [
    "#paramwellness",
    "#holisticliving",
    "#selfgrowth",
    "#wellbeing",
    "#mindfulaging",
        "#lifeafter30",
    "#healthtips",
    "#selfcare",
    "#mindbodybalance",

  ];

  const HashtagSection = ({ tags }) => (
    <div className="mt-6 flex flex-wrap md:gap-3 gap-1 justify-center md:mb-6 mb-3">
      {tags.map((tag, index) => (
        <span
          key={index}
          className="bg-light text-red-700 px-4 py-2 rounded-full text-sm font-semibold cursor-pointer transition-all duration-300 m-1 hover:bg-red-700 hover:text-white"
        >
          {tag}
        </span>
      ))}
    </div>
  );

  return (
    <>
    <Helmet>
  <title>Never Do These 7 Mistakes After 30 (Or You’ll Regret It Later)</title>
  <meta
    name="title"
    content="Never Do These 7 Mistakes After 30 (Or You’ll Regret It Later)"
  />
  <meta
    name="description"
    content="Avoid these 7 lifestyle mistakes after 30 to protect your energy, health, and peace of mind. Small changes now save big regrets later."
  />
  <meta
    name="keywords"
    content="lifestyle after 30, mistakes to avoid after 30, health tips for 30s, self care habits 30s, gut health, stress management, sleep habits, Ayurveda, Param Life, Indian wellness"
  />
</Helmet>

    <article className="max-w-4xl mx-auto mt-10 px-6 py-4 leading-relaxed">
      {/* Header */}
      <header className="mb-8 text-center">
        <h1 className="text-secondary text-4xl font-bold mb-4">
          Never Do These 7 Mistakes After 30
          <br />
          (Or You’ll Regret It Later)
        </h1>
        <img
          src="/assets/knowyourself/blogs/blog4.png"
          alt="After 30 wellness"
          className="w-[80%] max-w-xl h-auto max-h-[250px] rounded-lg shadow-md mx-auto object-cover"
        />
      </header>


      {/* Intro Section */}
      <section className="space-y-4 mb-8">
        <p>
          There’s something about crossing 30 that quietly changes how life feels. Your
          body becomes more honest. Your mind becomes more reflective. And suddenly, all
          the shortcuts you got away with in your 20s start asking for payback.
        </p>
        <p>
          The truth? Most of us don’t get tired because we’re getting older — we get tired
          because we keep ignoring what our body and mind have been whispering for years.
        </p>
        <p>
          If you’re someone who wants to age with energy, mental clarity, and peace — not
          just chase success and burn out — this one’s for you.
        </p>
        <p className="text-[#578d48] font-semibold">
          Let’s look at 7 simple mistakes most people make after 30 that slowly drain
          their vitality — and how you can fix them before they become habits.
        </p>
      </section>

      {/* Mistake 1 */}
      <section className="space-y-4 mb-8">
        <h2 className="text-primary text-2xl font-semibold">
          1. Ignoring Your Gut Health
        </h2>
        <p>
          Your gut is not just a digestion system — it’s your body’s second brain. When
          your gut is unhappy, it shows up as mood swings, low energy, brain fog, or even
          anxiety.
        </p>
        <p>
          Think about it — how often have you felt irritated or low right after overeating
          junk, skipping meals, or eating too fast? That’s not “just digestion.” That’s
          your body trying to tell you: <strong>“I can’t take this anymore.”</strong>
        </p>
        <p>
          A happy gut means balanced energy, better focus, and even calmer emotions.
        </p>
        <p className="text-[#578d48] font-semibold">Simple fix:</p>
        <ul className="list-disc list-inside space-y-1">
          <li>
            Start your day with warm water and a pinch of rock salt or lemon.
          </li>
          <li>Eat seasonal fruits.</li>
          <li>Include fermented foods like buttermilk, homemade pickles, or curd.</li>
          <li>Eat when you’re calm — not when you’re rushing.</li>
        </ul>
      </section>

      {/* Mistake 2 */}
      <section className="space-y-4 mb-8">
        <h2 className="text-primary text-2xl font-semibold">
          2. Skipping Your Daily Movement
        </h2>
        <p>
          After 30, your body doesn’t forgive long hours of sitting. No matter how healthy
          you eat — if you don’t move, your energy stagnates.
        </p>
        <p>
          Movement doesn’t mean gym only. It could be a 30-minute walk, 20 minutes of
          yoga, or dancing to your favourite playlist. Your body doesn’t care how you
          move — it just wants to move.
        </p>
        <p className="text-[#578d48] font-semibold">Remember:</p>
        <p>Motion creates emotion. You’ll never feel “mentally stuck” after a good walk.</p>
        <p className="text-[#578d48] font-semibold">Try this:</p>
        <p>
          Pick one kind of movement you enjoy. Do it daily for 10 minutes for a week.
          You’ll notice how your mood, sleep, and digestion start improving naturally.
        </p>
      </section>

      {/* Mistake 3 */}
      <section className="space-y-4 mb-8">
        <h2 className="text-primary text-2xl font-semibold">
          3. Staying Up Beyond 11 PM
        </h2>
        <p>
          We live in a “one more episode” generation. But after 30, your body clock becomes
          more sensitive to rhythm — not rebellion.
        </p>
        <p>
          Between 10 PM and 2 AM, your liver, brain, and hormones do deep repair work. If
          you miss that window, you may still sleep, but you won’t recover.
        </p>
        <p>You’ll wake up tired even after 8 hours of sleep.</p>
        <p className="text-[#578d48] font-semibold">Try this:</p>
        <ul className="list-disc list-inside space-y-1">
          <li>Dim your lights after 9:30 PM.</li>
          <li>Avoid screens 30 minutes before bed.</li>
          <li>Let your body know — it’s time to wind down.</li>
        </ul>
        <p>You’ll be shocked how much calmer and more focused you feel just by sleeping earlier.</p>
      </section>

      {/* Mistake 4 */}
      <section className="space-y-4 mb-8">
        <h2 className="text-primary text-2xl font-semibold">
          4. Eating Against Your Natural Body Type (Vata, Pitta, Kapha)
        </h2>
        <p>
          Ayurveda figured this out long before modern science did — not everyone thrives
          on the same diet.
        </p>
        <p>
          If you’re Vata dominant, you need grounding, warm, oily foods. If you’re Pitta,
          you need cooling, mild, non-spicy foods. If you’re Kapha, you need light, dry,
          and warming foods.
        </p>
        <p>
          When you eat against your nature, your digestion weakens — and when digestion
          weakens, everything else follows.
        </p>
        <p className="text-[#578d48] font-semibold">Try this:</p>
        <p>
          Observe what foods make you feel light, happy, and energetic after eating — not
          sleepy or bloated. That’s your body’s way of telling you what works.
        </p>
        <p className="text-[#578d48] font-semibold">
          Food is not just fuel; it’s information your body reads daily.
        </p>
      </section>


      {/* Mistake 5 */}
      <section className="space-y-4 mb-8">
        <h2 className="text-primary text-2xl font-semibold">5. Letting Stress Run Wild</h2>
        <p>
          At 30, life feels heavier — not because problems grow, but because your nervous
          system never got time to reset.
        </p>
        <p>
          Constant notifications, comparison, and deadlines keep your body in “fight or
          flight” mode. Over time, this becomes your normal.
        </p>
        <p>
          And when stress becomes normal, joy feels like a luxury. You start overthinking,
          reacting, or feeling empty even when things are fine.
        </p>
        <p className="text-[#578d48] font-semibold">Try this:</p>
        <ul className="list-disc list-inside space-y-1">
          <li>Add pauses in your day.</li>
          <li>Close your eyes. Breathe deeply. Feel your body soften.</li>
        </ul>
        <p>Stress dissolves in awareness — not in control.</p>
      </section>

      {/* Mistake 6 */}
      <section className="space-y-4 mb-8">
        <h2 className="text-primary text-2xl font-semibold">
          6. Neglecting Time With Loved Ones
        </h2>
        <p>
          In your 20s, it’s easy to chase ambition. But in your 30s, what actually heals
          you is connection — not achievement.
        </p>
        <p>
          You can buy comfort, but not the warmth of a shared laugh. You can earn freedom,
          but not the joy of being understood.
        </p>
        <p>
          Family, friends, or that one person who feels like home — they regulate your
          emotional system more than any therapy app ever will.
        </p>
        <p className="text-[#578d48] font-semibold">Try this:</p>
        <ul className="list-disc list-inside space-y-1">
          <li>Call someone who makes you laugh once a week.</li>
          <li>Have dinner without screens.</li>
          <li>Be fully present.</li>
        </ul>
        <p>The best investment after 30 is quality connection — not new possessions.</p>
      </section>

      {/* Mistake 7 */}
      <section className="space-y-4 mb-8">
        <h2 className="text-primary text-2xl font-semibold">
          7. Slipping Your Daily Unwinding Rituals
        </h2>
        <p>
          Your mornings may set the tone for the day — but your nights decide how
          peacefully it ends.
        </p>
        <p>
          If you scroll till midnight, your mind never shuts off. If you carry stress to
          bed, your body never truly rests.
        </p>
        <p>
          That’s why simple evening rituals matter — journaling, light stretching, prayer,
          or just silence for 10 minutes.
        </p>
        <p>
          This isn’t about being “productive.” It’s about returning home to yourself after
          a long day of giving.
        </p>
        <p className="text-[#578d48] font-semibold">Try this:</p>
        <p>
          Before sleeping, place your hand on your chest and take 5 slow breaths. Say to
          yourself, “I did enough today. I am enough.”
        </p>
        <p>You’ll sleep softer — and wake up stronger.</p>
      </section>


      {/* Closing Section */}
      <section className="space-y-4 mb-8">
        <h2 className="text-primary text-2xl font-semibold">
          The Truth Most People Realize Too Late
        </h2>
        <p>
          Your 30s aren’t the start of decline — they’re your body’s gentle reminder to
          realign. It’s not asking you to do more — it’s asking you to do what actually
          matters.
        </p>
        <p>
          Eat cleaner. Move daily. Sleep on time. Love deeper. That’s not “boring
          discipline.” That’s how peace returns.
        </p>
        <p className="text-[#578d48] font-semibold">
          And the more peaceful you become, the more powerful your life feels.
        </p>
      </section>

      {/* Final Section */}
      <section className="space-y-4">
        <h2 className="text-primary text-2xl font-semibold">Final Thought</h2>
        <p>
          If you’re over 30, here’s a simple reminder: You don’t need to rebuild your life
          overnight. Just start listening again — to your body, your breath, your
          intuition.
        </p>
        <p>
          It already knows what you need. You just need to stop doing what blocks it.
        </p>
        <p>
          So start today. Pick one of these 7 mistakes — and gently fix it. Your future
          self will thank you every single morning.
        </p>
      </section>
            {/* Hashtags Bottom */}
      <HashtagSection tags={hashtagsBottom} />


      <ShareSection />
    </article>
    </>
  );
};

export default Blog4;
